/* eslint-disable */
import React, { useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { requestUpdateSiteFiles, requestDeleteMonitoringFiles } from 'api/monitoring'

const FILE_LIMIT_MB = 50
const MAX_FILES = 10
const BYTES_PER_MB = 1024 * 1024

const fileSizeLimits = {
  ".zip": 50 * BYTES_PER_MB,
  ".shp": 50 * BYTES_PER_MB,
  ".geojson": 50 * BYTES_PER_MB,
  ".kml": 50 * BYTES_PER_MB,
  ".pdf": 50 * BYTES_PER_MB,
  ".doc": 50 * BYTES_PER_MB,
  ".docx": 50 * BYTES_PER_MB,
  ".xls": 50 * BYTES_PER_MB,
  ".xlsx": 50 * BYTES_PER_MB,
  ".csv": 50 * BYTES_PER_MB,
  ".jpeg": 50 * BYTES_PER_MB,
  ".jpg": 50 * BYTES_PER_MB,
  ".png": 50 * BYTES_PER_MB,
  ".bmp": 50 * BYTES_PER_MB,
  ".tiff": 50 * BYTES_PER_MB,
  ".webp": 50 * BYTES_PER_MB,
}


const validateFileSize = (file) => {
    const fileExtension = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2)
    const limit = fileSizeLimits[`.${fileExtension}`]
    if (!limit || file.size > limit) {
        return `This ${fileExtension} file is too large`
    }
    return null
}

export default function Uploader({params, siteFiles, disabled}) {
    const [files, setFiles] = useState(siteFiles.map(file => ({
        ...file,
        name: file.original_file_name
    })))
    const [uploadProgress, setUploadProgress] = useState({})
    const [totalSize, setTotalSize] = useState(0) // To track the total size of all files

    const handleFilesUploadsForSite = async(acceptedFiles) => {
        const formData = new FormData();

        // Append all files to formData
        acceptedFiles.forEach(file => {
            formData.append('files', file, file.name);
        });

        // Additional data can be added if necessary
        // Example: formData.append('siteName', 'New Site');

        // Call requestUpdateSite with formData
        await requestUpdateSiteFiles(params.monitoringId, formData);
    }

    const uploadFiles = useCallback(async(acceptedFiles) => {
        await handleFilesUploadsForSite(acceptedFiles);
    }, []);


    const onDrop = useCallback(async (acceptedFiles) => {
        if (disabled) return;

        if (files.length + acceptedFiles.length > 10) {
            alert('You can only upload up to 10 files.')
            return
        }

        let totalNewSize = 0
        acceptedFiles.forEach((file) => {
            totalNewSize += file.size
            const error = validateFileSize(file)
            if (error) {
                alert(error)
                return
            }
        })

        if (totalSize + totalNewSize > 50 * 1024 * 1024) {
            alert('The total size of all files should not exceed 50MB.')
            return
        }

        setTotalSize((prevSize) => {
            return prevSize + totalNewSize
        })

        // Update the local state for displaying files and their progress
        acceptedFiles.forEach((file) => {
            const interval = setInterval(() => {
                setUploadProgress((prevProgress) => {
                    let progress = (prevProgress[file.name] || 0) + 10
                    if (progress >= 100) {
                        clearInterval(interval)
                        progress = 100
                    }
                    return { ...prevProgress, [file.name]: progress }
                })
            }, 500)
            setFiles((prev) => {
                return [...prev, file]
            })
        })

        // Initiate the file upload
        await uploadFiles(acceptedFiles)
    }, [files, totalSize, uploadFiles, disabled])

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
      "application/zip": [".zip"],
      "application/x-shapefile": [".shp"],
      "application/geo+json": [".geojson"],
      "application/vnd.google-earth.kml+xml": [".kml"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"],
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "image/bmp": [".bmp"],
      "image/tiff": [".tiff"],
      "image/webp": [".webp"],
    },
    })

    const handleDelete = useCallback(async(fileName, isAlreadyUploaded = false, fileId) => {
        if (isAlreadyUploaded) {
            // Make your API call here to delete the file from the backend
            // Example: requestDeleteSiteFile(fileName);
            await requestDeleteMonitoringFiles(params.monitoringId, fileId)
        }

        setFiles((prev) => {
            return prev.filter((file) => {
                return file.name !== fileName
            })
        })
    }, [])

    useEffect(() => {
        return () => {
            // Clean up intervals if there are any left
            for (const key in uploadProgress) {
                if (uploadProgress[key] < 100) {
                    clearInterval(uploadProgress[key])
                }
            }
        }
    }, [uploadProgress])

    return (
        <div>
            <div  {...getRootProps({ disabled })} style={{
                border: '2px dashed gray',
                height: '100px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
            }}>
                {
                    !disabled && <input {...getInputProps()} />
                }
                {
                    disabled ?
                        "File uploading is disabled" :
                        "Drag & Drop your files or click to select files"
                }
            </div>
            <ul>
                {files.map((file, idx) => {
                    const isAlreadyUploaded = !!file.id; // Determines if the file is from siteFiles array

                    return (
                        <li key={idx} style={{ margin: '10px 0', position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>{file.name}</span>
                                {!isAlreadyUploaded && uploadProgress[file.name] !== 100 && (
                                    <>
                                        <div style={{
                                            marginLeft: '10px',
                                            height: '10px',
                                            width: '100px',
                                            border: '1px solid gray',
                                            position: 'relative'
                                        }}>
                                            <div style={{
                                                width: `${uploadProgress[file.name] || 0}%`,
                                                height: '100%',
                                                backgroundColor: 'green'
                                            }}></div>
                                        </div>
                                        <span style={{ marginLeft: '10px' }}>
                                            {uploadProgress[file.name] || 0}%
                                        </span>
                                    </>
                                )}
                            </div>
                            {
                                isAlreadyUploaded && (
                                <a
                                    href={disabled ? undefined : file.file_url}
                                    download={!disabled}
                                    onClick={(e) => {
                                        if (disabled) e.preventDefault();
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: disabled ? 0 : '30px', // adjusted to make space for the X button
                                        transform: 'translateY(-50%)',
                                        background: disabled ? 'gray' : 'blue',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        textAlign: 'center',
                                        textDecoration: 'none'
                                    }}
                                >
                                    ↓
                                </a>
                            )}
                            {
                                disabled ? null : (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(file.name, isAlreadyUploaded, file.id);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            right: 0,
                                            transform: 'translateY(-50%)',
                                            background: 'red',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        x
                                    </button>
                                )
                            }
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}